import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getDefaultRoute } from '../utils/permissions';
import type { UserRole } from '../types';

interface RoleRouteProps {
  /** Roles allowed to reach the nested routes. */
  allow: UserRole[];
}

/**
 * Route guard that blocks direct URL access to pages the current role
 * is not allowed to see (e.g. staff typing /dashboard, or a non-owner /users).
 * Users are redirected to the default landing page for their role.
 */
export function RoleRoute({ allow }: RoleRouteProps) {
  const { user } = useAuthStore();

  // ProtectedRoute already resolved the session; if the user is still
  // missing here, let the parent guard handle the redirect to /login.
  if (!user) {
    return <Outlet />;
  }

  // Owner is a superset of admin: any route open to admin is also open to owner.
  const effectiveAllow: UserRole[] =
    allow.includes('admin') && !allow.includes('owner') ? [...allow, 'owner'] : allow;

  if (!effectiveAllow.includes(user.role)) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  return <Outlet />;
}
